import { Config } from "@emed/virtual-lab-ui-shared/environment/config";
import { AuthContext } from "@emed/virtual-lab-ui-shared/providers/AuthProvider";
import { EnvironmentContext } from "@emed/virtual-lab-ui-shared/providers/EnvironmentProvider";
import { SiteContainer } from "@emed/virtual-lab-ui-shared/ui/SiteContainer";
import Head from "next/head";
import Error from "next/error";
import { useContext, useEffect } from "react";

type MenuEntry = {
	title?: string;
	description?: string;
	procedureID: string;
};

const DEMOS: MenuEntry[] = [
	{
		title: "Text below image (mobile view)",
		procedureID: "demo-text-below-image"
	},
	{
		title: "Disconnect and reconnect User",
		procedureID: "demo-reconnect-user"
	},
	{
		title: "Camera Demos",
		procedureID: "demo-photo-capture"
	},
	{
		title: "Detection Demos",
		procedureID: "demo-scanner"
	},
	{
		title: "Video Demos",
		procedureID: "demo-video-playback"
	},
	{
		title: "Forms Directory",
		procedureID: "demo-forms-sandbox"
	},
	{
		title: "Timer Demo",
		procedureID: "demo-timer"
	},
	{
		title: "Navigator Demo",
		procedureID: "demo-navigator-layout"
	}
];

const LABS: MenuEntry[] = [
	{
		procedureID: "us-emed-blood-collection-help"
	},
	{
		procedureID: "us-aon-glp1-check-in-refill"
	},
	{
		procedureID: "us-aon-glp1-check-in"
	},
	{
		procedureID: "us-aon-glp1-onboarding-tasso"
	},
	{
		procedureID: "us-glp1-check-in-refill"
	},
	{
		procedureID: "us-glp1-check-in"
	},
	{
		procedureID: "us-glp1-tasso-sales"
	},
	{
		procedureID: "us-glp1-tasso"
	},
	{
		procedureID: "us-p3-aon-glp1-check-in-refill"
	},
	{
		procedureID: "us-p3-aon-glp1-check-in"
	},
	{
		procedureID: "us-p3-aon-glp1-onboarding-tasso"
	},
	{
		procedureID: "weight-loss-check-in-refill-uk"
	},
	{
		procedureID: "weight-loss-check-in-refill"
	},
	{
		procedureID: "weight-loss-check-in-startup-uk-self-service"
	},
	{
		procedureID: "weight-loss-check-in-startup-uk"
	},
	{
		procedureID: "weight-loss-check-in-uk-self-service"
	},
	{
		procedureID: "weight-loss-check-in-uk"
	},
	{
		procedureID: "weight-loss-check-in"
	},
	{
		procedureID: "uk-randox-tasso"
	},
	{
		procedureID: "uk-shemed-glp1-6month-tasso"
	},
	{
		procedureID: "uk-shemed-glp1-check-in-self-service"
	},
	{
		procedureID: "uk-shemed-glp1-check-in"
	},
	{
		procedureID: "uk-shemed-glp1-onboarding-tasso"
	},
	{
		procedureID: "uk-shemed-standalone-blood-collection"
	},
	{
		procedureID: "uk-shemed-standalone-capture"
	},
	{
		procedureID: "tasso"
	},
	{
		procedureID: "tasso-anonymous"
	},
	{
		procedureID: "insurcheck-plus"
	},
	{
		procedureID: "cv-binah"
	},
	{
		procedureID: "cv-full-body"
	},
	{
		procedureID: "cv-id"
	},
	{
		procedureID: "cv-scale"
	},
	{
		procedureID: "us-shemed-glp1-self-service-poc"
	},
	{
		procedureID: "us-shemed-standalone-blood-collection"
	},
	{
		procedureID: "us-emed-blood-collection-help"
	}
];

export const IndexPage = () => {
	const EnvCtx = useContext(EnvironmentContext);
	const AuthCtx = useContext(AuthContext);
	/**
	 * Always logout first before starting
	 */
	useEffect(() => {
		if (EnvCtx.get(Config.FLAG_ENABLE_TRAINING_ENVIRONMENT, { optional: true })) {
			AuthCtx.logout();
		}
	}, []);
	if (!EnvCtx.get(Config.FLAG_ENABLE_TRAINING_ENVIRONMENT, { optional: true })) {
		return <Error statusCode={404} />;
	}

	const GLOBAL_PROCTOR_WEB_DOMAIN = EnvCtx.get(Config.GLOBAL_PROCTOR_WEB_DOMAIN);
	return (
		<SiteContainer centered title="Developer Home">
			<div className="container">
				<div className="columns">
					<div className="column">
						<div className="box">
							<div className="block">
								<h3 className="title is-size-4">Labs</h3>
							</div>
							<hr />
							<>
								{LABS.map((f, index) => (
									<div key={index} className="mt-4">
										<h4 className="title is-size-5 has-text-weight-normal px-3">{f.title ?? f.procedureID}</h4>
										<div className="columns">
											<div className="column has-text-centered">
												<a
													className="box navigator-option has-text-weight-light is-size-5 py-3 px-5"
													href={`/procedure/?pid=${f.procedureID}&sid=test_session`}
												>
													Adult
												</a>
											</div>
											<div className="column has-text-centered">
												<a
													className="box navigator-option has-text-weight-light is-size-5 py-3 px-5"
													href={`/procedure/?pid=${f.procedureID}&sid=dependent_test_session`}
												>
													Dependent
												</a>
											</div>
											{/*
											<div className="column has-text-centered is-narrow">
												<a
													className="box navigator-option has-text-weight-light is-size-5 py-3 px-5"
													href={`/procedure/${f.procedureID}/test_session/next`}
												>
													Next
												</a>
											</div> */}
										</div>
									</div>
								))}
							</>
						</div>
					</div>
					<div className="column is-narrow">
						<div className="box">
							<div className="block">
								<h3 className="title is-size-4">Proctor</h3>
							</div>
							<hr />
							<a href={GLOBAL_PROCTOR_WEB_DOMAIN}>Global Proctor UI</a>
						</div>
						<div className="box">
							<div className="block">
								<h3 className="title is-size-4">Testing Tools</h3>
							</div>
							<hr />
							<div className="py-3 px-5">
								<h4 className="title is-size-5 has-text-weight-normal px-3">PostMessage Test</h4>
								<p className="has-text-grey is-size-7 mb-3">
									Test parent frame communication with UserUI (mute mic, send to proctor, etc.)
								</p>
								<div className="columns">
									<div className="column has-text-centered">
										<a
											className="box navigator-option has-text-weight-light is-size-5 m-0"
											href="/test-postmessage"
										>
											Open Test Page
										</a>
									</div>
								</div>
							</div>
						</div>
						<div className="box">
							<h3 className="title is-size-4">Demos</h3>
							<hr />
							<>
								{DEMOS.map((e, index) => (
									<div key={index} className="py-3 px-5">
										<h4 className="title is-size-5 has-text-weight-normal px-3">{e.title ?? e.procedureID}</h4>
										<div className="columns">
											<div className="column has-text-centered">
												<a
													className="box navigator-option has-text-weight-light is-size-5 m-0"
													href={`/procedure/?pid=${e.procedureID}&sid=test_session`}
												>
													Legacy
												</a>
											</div>
											{/* <div className="column has-text-centered is-narrow">
												<a
													className="box navigator-option has-text-weight-light is-size-5 m-0"
													href={`/procedure/${e.procedureID}/test_session/next`}
												>
													Next
												</a>
											</div> */}
										</div>
									</div>
								))}
							</>
						</div>
					</div>
				</div>
			</div>
		</SiteContainer>
	);
};

export default IndexPage;
